<template>
<div class="chapter">
<div class="inner_page">
<div>
<p class="ct" id="toc_marker-8"><span class="ct "><a id="Anchor">A</a></span><a id="Anchor">lchemy</a></p><a id="Anchor">
<p class="txnoi">Some scholars say the name <span class="ital">alchemy</span> comes from the Greek <span class="ital">cheo</span>, meaning “I pour” or “I cast,” since much of alchemy has to do with the working of metals. But many believe the word comes from the Egyptian <span class="ital">Khem,</span> meaning “the black land” (land with black earth), and see that as indicating Egypt as alchemy’s place of origin. The Arabic article <span class="ital">al</span> was added to <span class="ital">Khem</span> to give <span class="ital">alchemy</span>. Later, as the science (some call it a pseudoscience) progressed, the article was again dropped, to become <span class="ital">chemistry</span>. Alchemy certainly is the early history of chemistry. </p>
<p class="tx">There was an early Egyptian alchemist whose name was Chemes. He wrote a book, called <span class="ital">Chema</span>, about his experiments trying to turn base metal into gold. Some few believe that the word alchemist comes from his name.</p>
<p class="tx"><span class="char-style-override-2">Whatever the origin of the word, it seems certain that the practice of alchemy had its beginnings in the Hellenistic culture of Alexandria, Egypt, which was the center of the world </span><span class="char-style-override-2">of learning at that time. In fact alchemy is a blending of Egyp</span><span class="char-style-override-2">tian technology, Greek philosophy, and Middle Eastern </span><span class="char-style-override-2">mysticism. The first alchemists were the metallurgical workers who prepared precious metals for the nobles but also produced cheap substitutes for the less affluent. These cheaper substitutes were often disguised to look like the more precious metals. It didn’t take long for the idea to develop that it might be possible to actually produce the precious metals themselves. This idea, in fact, was backed by Aristotle’s theory that there was a prime matter that was the basis for all substances. Astrology added the concept that the greater outer world of planets and stars reflected the inner world of humankind: a macrocosm and a microcosm. It was believed that under the proper astrological influences, it should be possible to change one metal into another; for example, lead into gold. In the same way that humankind perfected, going through death and rebirth, so might metals perfect and grow from one base form to another higher form. </span><span class="char-style-override-2"></span></p>
<p class="tx"><span class="char-style-override-2">The </span><span class="ital">Philosopher’s Stone</span><span class="char-style-override-2"> was the term given to a stone that—if it could be developed—would serve as the catalyst to transform metals and other raw material into gold. Although referred to as a stone, it was not necessarily an actual stone for it was believed that it might be a combination of fire and water, or other unlikely mixtures.</span></p>
</a><p class="tx"><a id="Anchor"><span class="char-style-override-2">So the original alchemy became an operation of passing substances through a series of chemical processes. The actual workings were noted, but in symbolic form to protect them from the dabblers and the uninitiated, and also to protect the alchemists themselves from charges by the Church that they were involved in heresy. The metals were represented by the astrological sign of the controlling body, and frequently the components and the actions were assimilated with Greek and Roman myths and mythological beings. The more the individual alchemists tried to hide and protect the results of their experiments, the more obtuse and confusing became much of what they did and said. In describing necessary actions, they used language such as: “When we marry the crowned king with the red daughter, she will conceive a son in the gentle fire . . . the dragon shuns the light of the sun, and our dead son shall live. The king comes forth from the fire and rejoices in the marriage.”</span><span class="fnref char-style-override-3"></span></a><a class="footnote-link" href="#/#footnote-237-1" id="footnote-237-1-backlink">1</a><span class="char-style-override-2"></span></p>
<p class="tx"><span class="char-style-override-2">Hermes Trismegistus, also known as “Thrice Great Hermes” (it is from his name that the term </span><span class="ital">the hermetic art</span><span class="char-style-override-2"> was given to alchemy), has been variously described as an earthly incarnation of the Egyptian god Thoth and as an Egyptian priest, or a pharaoh, who taught the Egyptians all their magic. He is credited with having written several thousand books, including the Emerald Tablet, or </span><span class="ital">Tabula Smaragdina</span><span class="char-style-override-2">, which contained all the hermetic teachings—the thirteen precepts—including the fundamental principles for the </span><span class="ital">Grand </span><span class="ital">Arcanum</span><span class="char-style-override-2">, or “great secret.” There are many references to the Emerald Tablet in alchemical writings. </span></p>
<p class="Bhead">Instruments</p>
<div class="x2x">
<img alt="01-crucible.tif" class="frame-1" src="image/01-crucible_fmt.png"/>
<p class="caption">Crucible</p></div>
<div class="x2x">
<img alt="02-dropper.tif" class="frame-1" src="image/02-dropper_fmt.png"/>
<p class="caption">Dropper</p></div>
<div class="x2x">
<img alt="03-grille.tif" class="frame-1" src="image/03-grille_fmt.png"/>
<p class="caption">Grille</p></div>
<div class="x2x">
<img alt="04-receiver.tif" class="frame-1" src="image/04-receiver_fmt.png"/>
<p class="caption"><span>Receiver </span></p></div>
<p class="caption"></p>
<p class="Bhead">Instruments <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="05-retort.tif" class="frame-1" src="image/05-retort_fmt.png"/>
<p class="caption">Retort</p></div>
<div class="x2x">
<img alt="06-scull.tif" class="frame-1" src="image/06-scull_fmt.png"/>
<p class="caption">Scull</p></div>
<div class="x2x">
<img alt="07-still.tif" class="frame-1" src="image/07-still_fmt.png"/>
<p class="caption">Still</p></div>
<div class="x2x">
<img alt="08-wick.tif" class="frame-1" src="image/08-wick_fmt.png"/>
<p class="caption">Wick</p></div>
<p class="caption"></p>
<p class="Bhead">Weights and Measures</p>
<div class="x2x">
<img alt="09-1ounce.tif" class="frame-1" src="image/09-1ounce_fmt.png"/>
<p class="caption">Ounce</p></div>
<div class="x2x">
<img alt="10-1dram.tif" class="frame-1" src="image/10-1dram_fmt.png"/>
<p class="caption">Dram</p></div>
<div class="x2x">
<img alt="11-1scruple.tif" class="frame-1" src="image/11-1scruple_fmt.png"/>
<p class="caption">Scruple</p></div>
<div class="x2x">
<img alt="12-1pinch.tif" class="frame-1" src="image/12-1pinch_fmt.png"/>
<p class="caption">Pinch</p></div>
<p class="caption"></p>
<p class="Bhead">Weights and Measures <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="13-1pint.tif" class="frame-1" src="image/13-1pint_fmt.png"/>
<p class="caption">Pint</p></div>
<div class="x2x">
<img alt="14-1pound.tif" class="frame-1" src="image/14-1pound_fmt.png"/>
<p class="caption">Pound</p></div>
<p class="caption"></p>
<p class="Bhead">Spirits</p>
<div class="x2x">
<img alt="15-copper.tif" class="frame-1" src="image/15-copper_fmt.png"/>
<p class="caption">Copper</p></div>
<div class="x2x">
<img alt="16-mercury.tif" class="frame-1" src="image/16-mercury_fmt.png"/>
<p class="caption">Mercury</p></div>
<div class="x2x">
<img alt="17-silver.tif" class="frame-1" src="image/17-silver_fmt.png"/>
<p class="caption">Silver</p></div>
<div class="x2x">
<img alt="18-tin.tif" class="frame-1" src="image/18-tin_fmt.png"/>
<p class="caption">Tin</p></div>
<div class="x2x">
<img alt="19-worldofspirit.tif" class="frame-1" src="image/19-worldofspirit_fmt.png"/>
<p class="caption-center">World of Spirit</p></div>
<p class="Bhead">Seasons</p>
<div class="x2x">
<img alt="20-spring.tif" class="frame-1" src="image/20-spring_fmt.png"/>
<p class="caption">Spring</p></div>
<div class="x2x">
<img alt="21-summer.tif" class="frame-1" src="image/21-summer_fmt.png"/>
<p class="caption">Summer</p></div>
<div class="x2x">
<img alt="22-autumn.tif" class="frame-1" src="image/22-autumn_fmt.png"/>
<p class="caption">Autumn</p></div>
<div class="x2x">
<img alt="23-winter.tif" class="frame-1" src="image/23-winter_fmt.png"/>
<p class="caption">Winter</p></div>
<p class="caption"></p>
<p class="Bhead">Materials</p>
<div class="x2x">
<img alt="24-gold.tif" class="frame-1" src="image/24-gold_fmt.png"/>
<p class="caption">Gold (i)</p></div>
<div class="x2x">
<img alt="25-gold.tif" class="frame-1" src="image/25-gold_fmt.png"/>
<p class="caption">Gold (ii)</p></div>
<div class="x2x">
<img alt="26-gold.tif" class="frame-1" src="image/26-gold_fmt.png"/>
<p class="caption">Gold (iii)</p></div>
<div class="x2x">
<img alt="27-gold.tif" class="frame-1" src="image/27-gold_fmt.png"/>
<p class="caption">Gold (iv)</p></div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="28-gold.tif" class="frame-1" src="image/28-gold_fmt.png"/>
<p class="caption">Gold (v)</p></div>
<div class="x2x">
<img alt="29-gold.tif" class="frame-1" src="image/29-gold_fmt.png"/>
<p class="caption">Gold (vi)</p></div>
<div class="x2x">
<img alt="30-gold.tif" class="frame-1" src="image/30-gold_fmt.png"/>
<p class="caption">Gold (vii)</p></div>
<div class="x2x">
<img alt="31-silver.tif" class="frame-1" src="image/31-silver_fmt.png"/>
<p class="caption">Silver (i)</p></div>
<div class="x2x">
<img alt="32-silver.tif" class="frame-1" src="image/32-silver_fmt.png"/>
<p class="caption">Silver (ii)</p></div>
<div class="x2x">
<img alt="33-silver.tif" class="frame-1" src="image/33-silver_fmt.png"/>
<p class="caption">Silver (iii)</p></div>
<div class="x2x">
<img alt="34-alum.tif" class="frame-1" src="image/34-alum_fmt.png"/>
<p class="caption">Alum</p></div>
<div class="x2x">
<img alt="35-antimony.tif" class="frame-1" src="image/35-antimony_fmt.png"/>
<p class="caption">Antimony</p></div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="36-arsenic_sulfur.tif" class="frame-1" src="image/36-arsenic_sulfur_fmt.png"/>
<p class="caption">Arsenic-Sulfur </p> </div>
<div class="x2x">
<img alt="37-aquavita.tif" class="frame-1" src="image/37-aquavita_fmt.png"/>
<p class="caption">Aqua Vitæ</p> </div>
<div class="x2x">
<img alt="38-bismuth.tif" class="frame-1" src="image/38-bismuth_fmt.png"/>
<p class="caption">Bismuth</p> </div>
<div class="x2x">
<img alt="39-borax.tif" class="frame-1" src="image/39-borax_fmt.png"/>
<p class="caption">Borax</p> </div>
<div class="x2x">
<img alt="40-brass.tif" class="frame-1" src="image/40-brass_fmt.png"/>
<p class="caption">Brass</p> </div>
<div class="x2x">
<img alt="41-burnedalum.tif" class="frame-1" src="image/41-burnedalum_fmt.png"/>
<p class="caption">Burned Alum</p> </div>
<div class="x2x">
<img alt="42-burnedhartshorn.tif" class="frame-1" src="image/42-burnedhartshorn_fmt.png"/>
<p class="caption">Burned Hartshorn</p> </div>
<div class="x2x">
<img alt="43-causticlime.tif" class="frame-1" src="image/43-causticlime_fmt.png"/>
<p class="caption">Caustic Lime</p> </div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="44-chalc.tif" class="frame-1" src="image/44-chalc_fmt.png"/>
<p class="caption">Chalc</p> </div>
<div class="x2x">
<img alt="45-cinder.tif" class="frame-1" src="image/45-cinder_fmt.png"/>
<p class="caption">Cinder</p> </div>
<div class="x2x">
<img alt="46-cinnibar.tif" class="frame-1" src="image/46-cinnibar_fmt.png"/>
<p class="caption">Cinnibar</p> </div>
<div class="x2x">
<img alt="47-clay.tif" class="frame-1" src="image/47-clay_fmt.png"/>
<p class="caption">Clay</p> </div>
<div class="x2x">
<img alt="48-copper.tif" class="frame-1" src="image/48-copper_fmt.png"/>
<p class="caption">Copper</p> </div>
<div class="x2x">
<img alt="49-cribbedashes.tif" class="frame-1" src="image/49-cribbedashes_fmt.png"/>
<p class="caption">Cribbled Ashes</p> </div>
<div class="x2x">
<img alt="50-crystal.tif" class="frame-1" src="image/50-crystal_fmt.png"/>
<p class="caption">Crystal</p> </div>
<div class="x2x">
<img alt="51-eggshells.tif" class="frame-1" src="image/51-eggshells_fmt.png"/>
<p class="caption">Eggshells</p> </div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="52-ginger.tif" class="frame-1" src="image/52-ginger_fmt.png"/>
<p class="caption">Ginger</p> </div>
<div class="x2x">
<img alt="53-glass.tif" class="frame-1" src="image/53-glass_fmt.png"/>
<p class="caption">Glass</p> </div>
<div class="x2x">
<img alt="54-gravel.tif" class="frame-1" src="image/54-gravel_fmt.png"/>
<p class="caption">Gravel</p> </div>
<div class="x2x">
<img alt="55-iron.tif" class="frame-1" src="image/55-iron_fmt.png"/>
<p class="caption">Iron</p> </div>
<div class="x2x">
<img alt="56-ironfillings.tif" class="frame-1" src="image/56-ironfillings_fmt.png"/>
<p class="caption">Iron Filings</p> </div>
<div class="x2x">
<img alt="57-lead.tif" class="frame-1" src="image/57-lead_fmt.png"/>
<p class="caption">Lead</p> </div>
<div class="x2x">
<img alt="58-lime.tif" class="frame-1" src="image/58-lime_fmt.png"/>
<p class="caption">Lime</p> </div>
<div class="x2x">
<img alt="59-magnesia.tif" class="frame-1" src="image/59-magnesia_fmt.png"/>
<p class="caption">Magnesia</p> </div>
<p class="caption"></p>
<p class="Bhead para-style-override-1">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="60-manure.tif" class="frame-1" src="image/60-manure_fmt.png"/>
<p class="caption">Manure</p> </div>
<div class="x2x">
<img alt="61-mercury.tif" class="frame-1" src="image/61-mercury_fmt.png"/>
<p class="caption">Mercury</p> </div>
<div class="x2x">
<img alt="62-nickel.tif" class="frame-1" src="image/62-nickel_fmt.png"/>
<p class="caption">Nickel</p> </div>
<div class="x2x">
<img alt="63-niterflowers.tif" class="frame-1" src="image/63-niterflowers_fmt.png"/>
<p class="caption">Niter Flowers </p> </div>
<div class="x2x">
<img alt="64-niteroil.tif" class="frame-1" src="image/64-niteroil_fmt.png"/>
<p class="caption">Niter Oil</p> </div>
<div class="x2x">
<img alt="65-nitricacid.tif" class="frame-1" src="image/65-nitricacid_fmt.png"/>
<p class="caption">Nitric Acid</p> </div>
<div class="x2x">
<img alt="66-potash.tif" class="frame-1" src="image/66-potash_fmt.png"/>
<p class="caption">Potash</p> </div>
<div class="x2x">
<img alt="67-redarsenic.tif" class="frame-1" src="image/67-redarsenic_fmt.png"/>
<p class="caption">Red Arsenic</p> </div>
<p class="caption"></p>
<p class="Bhead ">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="68-rocksalt.tif" class="frame-1" src="image/68-rocksalt_fmt.png"/>
<p class="caption">Rock Salt</p></div>
<div class="x2x">
<img alt="69-seasalt.tif" class="frame-1" src="image/69-seasalt_fmt.png"/>
<p class="caption">Sea Salt</p></div>
<div class="x2x">
<img alt="70-soapstone.tif" class="frame-1" src="image/70-soapstone_fmt.png"/>
<p class="caption">Soapstone</p></div>
<div class="x2x">
<img alt="71-soot.tif" class="frame-1" src="image/71-soot_fmt.png"/>
<p class="caption">Soot</p></div>
<div class="x2x">
<img alt="72-steel.tif" class="frame-1" src="image/72-steel_fmt.png"/>
<p class="caption">Steel</p></div>
<div class="x2x">
<img alt="73-stone.tif" class="frame-1" src="image/73-stone_fmt.png"/>
<p class="caption">Stone</p></div>
<div class="x2x">
<img alt="74-sugar.tif" class="frame-1" src="image/74-sugar_fmt.png"/>
<p class="caption">Sugar</p></div>
<div class="x2x">
<img alt="75-sulfur.tif" class="frame-1" src="image/75-sulfur_fmt.png"/>
<p class="caption">Sulfur</p></div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="76-tin.tif" class="frame-1" src="image/76-tin_fmt.png"/>
<p class="caption">Tin</p></div>
<div class="x2x">
<img alt="77-urine.tif" class="frame-1" src="image/77-urine_fmt.png"/>
<p class="caption">Urine</p></div>
<div class="x2x">
<img alt="78-verdigris.tif" class="frame-1" src="image/78-verdigris_fmt.png"/>
<p class="caption">Verdigris</p></div>
<div class="x2x">
<img alt="79-vinegar.tif" class="frame-1" src="image/79-vinegar_fmt.png"/>
<p class="caption">Vinegar</p></div>
<div class="x2x">
<img alt="80-vitriol.tif" class="frame-1" src="image/80-vitriol_fmt.png"/>
<p class="caption">Vitriol</p></div>
<div class="x2x">
<img alt="81-whitearsenic.tif" class="frame-1" src="image/81-whitearsenic_fmt.png"/>
<p class="caption">White Arsenic</p></div>
<div class="x2x">
<img alt="82-winespirit.tif" class="frame-1" src="image/82-winespirit_fmt.png"/>
<p class="caption">Wine Spirit</p></div>
<div class="x2x">
<img alt="83-wood.tif" class="frame-1" src="image/83-wood_fmt.png"/>
<p class="caption">Wood</p></div>
<p class="caption"></p>
<p class="Bhead">Materials <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="84-yellowarsenic.tif" class="frame-1" src="image/84-yellowarsenic_fmt.png"/>
<p class="caption">Yellow Arsenic</p></div>
<div class="x2x">
<img alt="85-yellowwax.tif" class="frame-1" src="image/85-yellowwax_fmt.png"/>
<p class="caption">Yellow Wax</p></div>
<div class="x2x">
<img alt="86-zinc.tif" class="frame-1" src="image/86-zinc_fmt.png"/>
<p class="caption-center">Zinc</p></div>
<p class="Bhead">Processes</p>
<div class="x2x">
<img alt="87-blackening.tif" class="frame-1" src="image/87-blackening_fmt.png"/>
<p class="caption">Blackening (melanosis)</p></div>
<div class="x2x">
<img alt="88-whitening.tif" class="frame-1" src="image/88-whitening_fmt.png"/>
<p class="caption">Whitening (leucosis)</p></div>
<div class="x2x">
<img alt="89-yellowing.tif" class="frame-1" src="image/89-yellowing_fmt.png"/>
<p class="caption">Yellowing (xanthosis)</p></div>
<div class="x2x">
<img alt="90-reddening.tif" class="frame-1" src="image/90-reddening_fmt.png"/>
<p class="caption">Reddening (iosis)</p></div>
<p class="caption"></p>
<p class="Bhead">Processes <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="91-amalgam.tif" class="frame-1" src="image/91-amalgam_fmt.png"/>
<p class="caption">Amalgam</p> </div>
<div class="x2x">
<img alt="92-amalgamation.tif" class="frame-1" src="image/92-amalgamation_fmt.png"/>
<p class="caption">Amalgamation</p> </div>
<div class="x2x">
<img alt="93-toboil.tif" class="frame-1" src="image/93-toboil_fmt.png"/>
<p class="caption">Boil</p> </div>
<div class="x2x">
<img alt="94-tocompose.tif" class="frame-1" src="image/94-tocompose_fmt.png"/>
<p class="caption">Compose</p> </div>
<div class="x2x">
<img alt="95-distill.tif" class="frame-1" src="image/95-distill_fmt.png"/>
<p class="caption">Distill</p> </div>
<div class="x2x">
<img alt="96-essence.tif" class="frame-1" src="image/96-essence_fmt.png"/>
<p class="caption">Essence</p> </div>
<div class="x2x">
<img alt="97-fumes.tif" class="frame-1" src="image/97-fumes_fmt.png"/>
<p class="caption">Fumes</p> </div>
<div class="x2x">
<img alt="98-filter.tif" class="frame-1" src="image/98-filter_fmt.png"/>
<p class="caption">Filter</p> </div>
<p class="caption"></p>
<p class="Bhead">Processes <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="99-tomix.tif" class="frame-1" src="image/99-tomix_fmt.png"/>
<p class="caption">Mix</p> </div>
<div class="x2x">
<img alt="100-topowder.tif" class="frame-1" src="image/100-topowder_fmt.png"/>
<p class="caption">Powder</p> </div>
<div class="x2x">
<img alt="101-topurify.tif" class="frame-1" src="image/101-topurify_fmt.png"/>
<p class="caption">Purify</p> </div>
<div class="x2x">
<img alt="102-topulverize.tif" class="frame-1" src="image/102-topulverize_fmt.png"/>
<p class="caption">Pulverize</p> </div>
<div class="x2x">
<img alt="103-torot.tif" class="frame-1" src="image/103-torot_fmt.png"/>
<p class="caption">Rot</p> </div>
<div class="x2x">
<img alt="104-tosolve.tif" class="frame-1" src="image/104-tosolve_fmt.png"/>
<p class="caption">Solve</p> </div>
<div class="x2x">
<img alt="105-sublime.tif" class="frame-1" src="image/105-sublime_fmt.png"/>
<p class="caption">Sublime</p> </div>
<div class="x2x">
<img alt="106-take.tif" class="frame-1" src="image/106-take_fmt.png"/>
<p class="caption">Take</p> </div>
<p class="caption"></p>
<p class="Bhead">Processes <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="107-torrefactiongold.tif" class="frame-1" src="image/107-torrefactiongold_fmt.png"/>
<p class="caption">Torrefaction of Gold </p> </div>
<div class="x2x">
<img alt="108-torrefactionsilver.tif" class="frame-1" src="image/108-torrefactionsilver_fmt.png"/>
<p class="caption">Torrefaction of Silver</p> </div>
<p class="caption"></p>
<p class="Bhead para-style-override-1">Animals, Birds, etc.</p>
<div class="x2x">
<img alt="109-blackbird.tif" class="frame-1" src="image/109-blackbird_fmt.png"/>
<p class="caption">Blackbird (blackened mass)</p> </div>
<div class="x2x">
<img alt="110-lion.tif" class="frame-1" src="image/110-lion_fmt.png"/>
<p class="caption">Lion (acid)</p> </div>
<div class="x2x">
<img alt="111-moonlunar.tif" class="frame-1" src="image/111-moonlunar_fmt.png"/>
<p class="caption">Moon/Lunar</p> </div>
<div class="x2x">
<img alt="112-competition.tif" class="frame-1" src="image/112-competition_fmt.png"/>
<p class="caption">Ouroboros (completion)</p> </div>
<p class="caption"></p>
<p class="Bhead">Animals, Birds, etc. <span class="ital">(continued)</span></p>
<div class="x2x">
<img alt="113-stag.tif" class="frame-1" src="image/113-stag_fmt.png"/>
<p class="caption">Stag (soul)</p></div>
<div class="x2x">
<img alt="114-sunsol.tif" class="frame-1" src="image/114-sunsol_fmt.png"/>
<p class="caption">Sun/Sol</p></div>
<div class="x2x">
<img alt="115-unicorn.tif" class="frame-1" src="image/115-unicorn_fmt.png"/>
<p class="caption">Unicorn (spirit)</p></div>
<div class="x2x">
<img alt="116-birds.tif" class="frame-1" src="image/116-birds_fmt.png"/>
<p class="caption">Birds (volatilization)</p></div>
<p class="contents"><a href="#/Contents#x-contents-">[contents]</a></p>
<div class="footnotes">
<div class="footnote">
<p class="fn"><a class="footnote-anchor" href="#/#footnote-237-1-backlink" id="footnote-237-1">1</a>. <span class="ital">Tractatus aureus</span> (1610). The <span class="ital">Tractatus aureus </span>is an alchemical treatise in seven chapters attributed to Hermes Trismegistus.</p>
</div>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// SSO.vue?f3800630